import React from "react";
import { connect } from "react-redux";
import {
  decodeJwt,
  isJwtMultiFactorAuthenticated,
  isJwtValid,
  readExternalSignInForThisEnvironment,
} from "../../common/identity/authUtils";
import { isEmpty, isFull } from "../../common/utils";
import { logout } from "../../store/reducers/accountService";
import { saveToken } from "../../store/reducers/accountService";
import { fetchGrantedClaims } from "../../services/business/accessService";
import {
  TenantGrantCatalogue,
  OrganisationGrantCatalogue,
  AppSpaceGrantCatalogue,
  AppGrantCatalogue,
  getAllClaimsFromGrantCatalogue,
  getResourceTypeFromGrantCatalogue,
  buildPermissionsFromGrantCatalogue,
} from "../../common/identity/grants";
import { startUserGrantsFetch, finishUserGrantsFetch } from "../../store/reducers/accessService";

/**
 * Authentication and authorisation.
 *
 * WithAuth(Component) injects the session (user, tokens, JWT claims) plus the
 * user's grants: the TenantGrantCatalogue / OrganisationGrantCatalogue / AppSpaceGrantCatalogue
 * catalogues are resolved once per user + active tenant/org/appspace selection
 * (stored in the redux `access` slice, re-rendered by react-redux) and read as
 * props.permissions.tenant / .organisation / .appSpace. props.permissionsResolving
 * is true while there are no valid verdicts yet. Screens declare nothing.
 *
 * Screens about one specific resource declare only which one:
 * WithAuth(Component, { resource: (props) => ({ productId: ... }) }). The
 * catalogue is inferred from the most specific id (productId -> AppGrantCatalogue,
 * appSpaceId -> AppSpaceGrantCatalogue, organisationId -> OrganisationGrantCatalogue); its
 * flags resolve in their own call, re-run when the ids change, and inject flat
 * on props.permissions. An explicit `grantCatalogue` option overrides the inference.
 */

// Each mount revalidates the user grants; this freshness window makes the
// burst of components mounting together share one fetch, and existing
// verdicts stay on screen while a revalidation is in flight.
const USER_GRANTS_FRESH_MS = 2000;

// Dispatches land in the store synchronously, but the components mounting in
// the same render pass still hold the previous props; this set stops that
// burst from starting duplicate fetches.
const inFlightUserGrantKeys = new Set();

export default function WithAuth(WrappedComponent, options) {
  // A screen's own declaration: which specific resource its flags apply to.
  // User grants for the active selection need no declaration.
  const screenDeclaration = options?.grantCatalogue || options?.resource ? options : undefined;

  // The declared resource implies its catalogue: the most specific id names the
  // tier. An explicit options.grantCatalogue overrides the inference.
  const grantCatalogueForDeclaredResource = (resourceIds) => {
    if (options?.grantCatalogue) return options.grantCatalogue;
    if (isFull(resourceIds.productId)) return AppGrantCatalogue;
    if (isFull(resourceIds.appSpaceId)) return AppSpaceGrantCatalogue;
    if (isFull(resourceIds.organisationId)) return OrganisationGrantCatalogue;
    return TenantGrantCatalogue;
  };

  // ...and returns another component...
  class WithAuthHoC extends React.Component {

    state = {
      declaredResourceGrantedClaims: [],
      permissionsResolving: Boolean(screenDeclaration),
    };

    // The ids the screen's own declaration names, e.g. { productId: ... }.
    // Empty when the screen declared nothing.
    declaredResourceIds = (props = this.props) => {
      return screenDeclaration?.resource ? screenDeclaration.resource(props) : {};
    };

    // The resource the verdicts apply to: the active tenant/org/appspace
    // selection, plus (and overridden by) the screen's declared ids.
    resolveResourceIds = (props = this.props) => {
      return {
        appSpaceId: props.organisations?.activeAppSpaceId,
        organisationId: props.organisations?.activeOrganisationId,
        tenantId: props.tenants?.activeTenantId,
        ...this.declaredResourceIds(props),
      };
    };

    // The catalogue the declared screen's flags come from, inferred from the
    // declared ids only; the active selection must not sway the inference.
    declaredGrantCatalogue = () => {
      return grantCatalogueForDeclaredResource(this.declaredResourceIds());
    };

    fetchGrantedClaimsForDeclaredResource = async () => {
      const user = this.getUser();
      const grantCatalogue = this.declaredGrantCatalogue();
      const requestedFor = JSON.stringify({ userId: user?.userId, ...this.resolveResourceIds() });

      const grantedClaims = await fetchGrantedClaims({
        userId: user?.userId,
        candidateClaims: getAllClaimsFromGrantCatalogue(grantCatalogue),
        targetResourceType: getResourceTypeFromGrantCatalogue(grantCatalogue),
        ...this.resolveResourceIds(),
        accessToken: user?.accessToken,
        refreshToken: user?.refreshToken,
      });

      // The user or resource ids changed while this fetch was in flight: the
      // response answers a superseded question, and the fetch componentDidUpdate
      // started for the new ids commits instead (same later-wins rule as the
      // user-grants reducer).
      const currentFor = JSON.stringify({ userId: this.getUser()?.userId, ...this.resolveResourceIds() });
      if (requestedFor !== currentFor) {
        return;
      }

      this.setState({
        declaredResourceGrantedClaims: Array.isArray(grantedClaims) ? grantedClaims : [],
        permissionsResolving: false,
      });
    };

    // Assembles the props.permissions value every wrapped component reads.
    buildPermissionsProp = () => {
      const userGrants = this.props.userGrants ?? {};
      const grantedClaims =
        userGrants.verdictsKey === this.userGrantsKey() ? userGrants.grantedClaims : [];

      const permissions = {
        tenant: buildPermissionsFromGrantCatalogue(TenantGrantCatalogue, grantedClaims),
        organisation: buildPermissionsFromGrantCatalogue(OrganisationGrantCatalogue, grantedClaims),
        appSpace: buildPermissionsFromGrantCatalogue(AppSpaceGrantCatalogue, grantedClaims),
      };

      // A screen's own declaration (a specific product, a card's own resource)
      // resolves in its own call; its flags merge in flat beside the tenant/org/appSpace maps.
      return screenDeclaration
        ? { ...permissions, ...buildPermissionsFromGrantCatalogue(this.declaredGrantCatalogue(), this.state.declaredResourceGrantedClaims) }
        : permissions;
    };

    isResolvingPermissions = () => {
      if (screenDeclaration) {
        return this.state.permissionsResolving;
      }

      // Only block rendering while we have NO valid verdicts for this key;
      // a same-key revalidation keeps the current verdicts on screen.
      return (
        isFull(this.props.tenants?.activeTenantId) &&
        this.props.userGrants?.verdictsKey !== this.userGrantsKey()
      );
    };

    userGrantsKey = () => {
      // Keyed by user id as well as the selected tenant/org/appspace, so a
      // re-login in the same session never serves the previous user's verdicts.
      return [
        this.getUser()?.userId,
        this.props.tenants?.activeTenantId,
        this.props.organisations?.activeOrganisationId,
        this.props.organisations?.activeAppSpaceId,
      ].join("|");
    };

    // Fetches the signed-in user's granted claims (for their active
    // tenant/org/appspace selection) into the `access` slice, unless a fetch
    // for this exact key is already in flight, the stored verdicts are younger
    // than USER_GRANTS_FRESH_MS, or this is a mere re-render (revalidate is
    // only passed from componentDidMount, i.e. a screen load).
    refreshUserGrantsIfStale = async ({ revalidate } = {}) => {
      const user = this.getUser();
      const tenantId = this.props.tenants?.activeTenantId;
      const key = this.userGrantsKey();
      const userGrants = this.props.userGrants ?? {};

      if (isEmpty(user) || isEmpty(tenantId) || inFlightUserGrantKeys.has(key)) {
        return;
      }

      const sameKey = userGrants.fetchKey === key;
      const freshEnough =
        sameKey && Date.now() - userGrants.resolvedAt < USER_GRANTS_FRESH_MS;

      if ((sameKey && !revalidate) || (sameKey && freshEnough)) {
        return;
      }

      inFlightUserGrantKeys.add(key);
      this.props.startUserGrantsFetch(key);

      // The finally clause guarantees the key never stays marked in-flight: a
      // rejected fetch would otherwise block every future refresh for this key
      // until a full page reload.
      try {
        const request = {
          tenantId,
          organisationId: this.props.organisations?.activeOrganisationId,
          appSpaceId: this.props.organisations?.activeAppSpaceId,
          userId: user?.userId,
          accessToken: user?.accessToken,
          refreshToken: user?.refreshToken,
        };

        const [tenantClaims, organisationClaims, appSpaceClaims] = await Promise.all([
          fetchGrantedClaims({ ...request, candidateClaims: getAllClaimsFromGrantCatalogue(TenantGrantCatalogue), targetResourceType: getResourceTypeFromGrantCatalogue(TenantGrantCatalogue) }),
          fetchGrantedClaims({ ...request, candidateClaims: getAllClaimsFromGrantCatalogue(OrganisationGrantCatalogue), targetResourceType: getResourceTypeFromGrantCatalogue(OrganisationGrantCatalogue) }),
          fetchGrantedClaims({ ...request, candidateClaims: getAllClaimsFromGrantCatalogue(AppSpaceGrantCatalogue), targetResourceType: getResourceTypeFromGrantCatalogue(AppSpaceGrantCatalogue) }),
        ]);

        // A later key change wins; the reducer only commits if still current.
        this.props.finishUserGrantsFetch(key, [
          ...tenantClaims,
          ...organisationClaims,
          ...appSpaceClaims,
        ]);
      } finally {
        inFlightUserGrantKeys.delete(key);
      }
    };

    componentDidMount() {
      // Every screen load revalidates; the freshness window coalesces the burst
      // of components mounting together into one fetch.
      this.refreshUserGrantsIfStale({ revalidate: true });

      if (screenDeclaration) {
        this.fetchGrantedClaimsForDeclaredResource();
      }
    }

    componentDidUpdate(prevProps) {
      this.refreshUserGrantsIfStale();

      if (!screenDeclaration) {
        return;
      }

      const previous = { userId: prevProps.user?.userId, ...this.resolveResourceIds(prevProps) };
      const current = { userId: this.props.user?.userId, ...this.resolveResourceIds() };
      if (JSON.stringify(previous) !== JSON.stringify(current)) {
        this.setState({ permissionsResolving: true });
        this.fetchGrantedClaimsForDeclaredResource();
      }
    }

    getUser = () => {
      if (this.isAuthenticationValid(this.props.user)) {
        //Add claims to the user
        let augmentedUser = {
          ...this.props.user,
          claims: decodeJwt(this.props.user.accessToken),
          teams: this.props.user?.teams
        }

        return augmentedUser;
      }
      return undefined;
    }

    saveToken = (user) => {
      if (isFull(user)) {
        this.props.saveToken(user);
      }
    }

    getClaim = (claim) => {
      if (isEmpty(claim)) {
        return undefined;
      }

      let userHolder = this.getUser();
      return userHolder?.claims[claim];
    }

    isAuthenticationValid = (userInput) => {
      if (isFull(userInput)) {
        let user = userInput;
        if (isJwtValid(user?.accessToken)) {
          return true;
        }
      } else {
        let user = readExternalSignInForThisEnvironment()?.user;
        if (isJwtValid(user?.accessToken)) {
          return true;
        }
      }
      return undefined;
    }

    isUserMultiFactorAuthenticated = (userInput) => {
      if (isFull(userInput)) {
        let user = userInput;
        if (isJwtMultiFactorAuthenticated(user?.accessToken)) {
          return true;
        }
      } else {
        let user = readExternalSignInForThisEnvironment()?.user;
        if (isJwtMultiFactorAuthenticated(user?.accessToken)) {
          return true;
        }
      }
      return false;
    }

    render() {
      return (
        <WrappedComponent
          isAuthenticated={this.isAuthenticationValid(this.props.user)}
          isTwoFactorAuthenticated={this.isUserMultiFactorAuthenticated(this.props.user)}
          permissionsResolving={this.isResolvingPermissions()}
          permissions={this.buildPermissionsProp()}
          user={this.getUser()}
          saveToken={this.saveToken}
          getClaim={this.getClaim}
          logout={() => {
            this.props.logout({
              accessToken: this.getUser()?.accessToken,
              refreshToken: this.getUser()?.refreshToken,
            });
          }}
          isAuthenticationValid={this.isAuthenticationValid}
          accessToken={this.getUser()?.accessToken}
          refreshToken={this.getUser()?.refreshToken}
          {...this.props}
        />
      );
    }
  }

  const mapStateToProps = (state) => ({
    user: state.entities.accounts.membership.user,
    userGrants: state.entities.access,
    // Also mapped by WithOrganisations, but WithAuth is often used standalone
    // (auth/profile screens) and needs the active selection itself to key and
    // refresh the user grants. Same store references, so no duplication cost.
    organisations: state.entities.organisations,
    tenants: state.entities.tenants,
  });

  const mapDispatchToProps = (dispatch) => ({
    logout: ({ accessToken, refreshToken }) =>
      dispatch(logout({ accessToken, refreshToken })),
    saveToken: (user) => {
      dispatch(saveToken(user));
    },
    startUserGrantsFetch: (key) => {
      dispatch(startUserGrantsFetch(key));
    },
    finishUserGrantsFetch: (key, grantedClaims) => {
      dispatch(finishUserGrantsFetch(key, grantedClaims));
    }
  });

  return connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true })(WithAuthHoC);
}
